import React, { useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import './Auth.css';

export default function ForgotPassword({ onSwitchToSignIn }) {
  const { forgotPassword, confirmForgotPassword, isLoading, error, clearError } = useAuthContext();
  
  const [username, setUsername] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setValidationErrors({ username: 'Username or email is required' });
      return;
    }
    
    // Clear previous errors and messages
    setValidationErrors({});
    setSuccessMessage('');
    clearError();
    
    try {
      const result = await forgotPassword(username.trim());
      setShowConfirmation(true);
      setSuccessMessage(result?.message || 'A verification code has been sent to your email');
      setValidationErrors({});
    } catch (err) {
      console.error('Forgot password error:', err);
      // Extract error message from the error object
      let errorMessage = 'Failed to send verification code. Please check your username/email and try again.';
      
      // Check for specific error patterns in the error object
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (err?.originalError) {
        // Check the original error message for more specific information
        const originalMsg = err.originalError.message || err.originalError.toString() || '';
        if (originalMsg.toLowerCase().includes('cannot reset password') && 
            (originalMsg.toLowerCase().includes('no registered') || originalMsg.toLowerCase().includes('no verified'))) {
          errorMessage = 'Cannot reset password. This account does not have a verified email address. Please contact support or use a different account.';
        } else if (originalMsg.toLowerCase().includes('user does not exist') || originalMsg.toLowerCase().includes('user not found')) {
          errorMessage = 'User not found. Please check your username or email and try again.';
        } else {
          errorMessage = originalMsg || err.message || errorMessage;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setValidationErrors({ username: errorMessage });
      setSuccessMessage('');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      await confirmForgotPassword(username, confirmationCode, newPassword);
      setSuccessMessage('Password changed successfully. You can now sign in.');
      
      // Clear form and redirect to sign in after a delay
      setTimeout(() => {
        setShowConfirmation(false);
        setUsername('');
        setConfirmationCode('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccessMessage('');
        onSwitchToSignIn();
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!confirmationCode.trim()) {
      errors.confirmationCode = 'Verification code is required';
    }
    
    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const handleInputChange = (field, value) => {
    if (field === 'username') {
      setUsername(value);
    } else if (field === 'confirmationCode') {
      setConfirmationCode(value);
    } else if (field === 'newPassword') {
      setNewPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  if (showConfirmation) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <h2>Reset Password</h2>
          <p>Enter the verification code and your new password</p>
        </div>

        {successMessage && (
          <div className="auth-success">
            <i className="fas fa-check-circle"></i>
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handlePasswordReset} className="auth-form">
          <div className="form-group">
            <label htmlFor="confirmationCode">Verification Code</label>
            <input
              type="text"
              id="confirmationCode"
              value={confirmationCode}
              onChange={(e) => handleInputChange('confirmationCode', e.target.value)}
              className={getFieldError('confirmationCode') ? 'error' : ''}
              placeholder="Enter the verification code"
              disabled={isLoading}
              autoFocus
            />
            {getFieldError('confirmationCode') && (
              <span className="error-message">{getFieldError('confirmationCode')}</span>
            )}
          </div>

          <div className="form-group has-toggle">
            <label htmlFor="newPassword">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              className={getFieldError('newPassword') ? 'error' : ''}
              placeholder="Create a new secure password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowNewPassword(!showNewPassword)}
              disabled={isLoading}
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              <i className={showNewPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
            </button>
            {getFieldError('newPassword') && (
              <span className="error-message">{getFieldError('newPassword')}</span>
            )}
          </div>

          <div className="form-group has-toggle">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={getFieldError('confirmPassword') ? 'error' : ''}
              placeholder="Confirm your new password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={isLoading}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
            </button>
            {getFieldError('confirmPassword') && (
              <span className="error-message">{getFieldError('confirmPassword')}</span>
            )}
          </div>

          {error && (
            <div className="auth-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Changing password...
              </>
            ) : (
              <>
                <i className="fas fa-key"></i>
                Change Password
              </>
            )}
          </button>
        </form>

        <div className="auth-links">
          <button
            type="button"
            className="link-button"
            onClick={() => setShowConfirmation(false)}
            disabled={isLoading}
          >
            Back to start
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <h2>Forgot Your Password?</h2>
        <p>Enter your username to receive a verification code</p>
      </div>

        <form onSubmit={handleUsernameSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="username">Username or Email</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="Enter your username or email"
            disabled={isLoading}
            autoFocus
            className={getFieldError('username') ? 'error' : ''}
          />
          {getFieldError('username') && (
            <span className="error-message">{getFieldError('username')}</span>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="auth-button primary"
          disabled={isLoading || !username.trim()}
        >
          {isLoading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Sending code...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane"></i>
              Send Verification Code
            </>
          )}
        </button>
      </form>

      <div className="auth-links">
        <button
          type="button"
          className="link-button"
          onClick={onSwitchToSignIn}
          disabled={isLoading}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
